"use client";
import { useCallback, useEffect, useState } from "react";

// Bekleyen bir arka plan işini (süpervizör raporu, özel hasta üretimi) izleyen
// yoklayıcı (F6): localStorage'daki kaydı okur, hazır değilse aralıklı yoklar,
// beat'i (running → ready / failed / ...) ve beat başına kapatma durumunu tutar.
// İki watcher'ın ortak makinesi; içerik ve yoklama çağrısı onlarda kalır.
//
// Kayıt yalnızca mount'ta değil, aynı sekmedeki yazım/silme olayı ve diğer
// sekmelerin `storage` olayıyla da yeniden okunur (F5: kabuk artık sayfalar
// arasında kurulu kalır, watcher'ın "yeni kayıt geldi"yi kendisi görmesi gerekir).
//
// Kayıt başka bir yerde silinince (rapor açıldı, başka sekme onayladı) canlı beat
// kapanır; terminal beat (failed / too_short) kalır, çünkü kaydı yoklayıcının
// kendisi sildi ve toast öğrencinin tek tutamağıdır.

const POLL_INTERVAL_MS = 5000;
// İlk yoklama neredeyse hemen: iş az önce başlatıldı, cevap çoğunlukla bir kısa
// tur uzaklıkta.
const FIRST_POLL_MS = 800;

export type PollResult<K extends string> =
  // Terminal beat: yoklama durur, toast bu beat'e geçer.
  | { beat: K }
  // Devam: aralık sonra yeniden yoklanır.
  | { again: true }
  // Kayıt bayat (oturum değişti, iş kayboldu): unutulur, toast gösterilmez.
  | { drop: true };

type Beat<E, K extends string> = { id: string | number; entry: E; kind: K; dismissed: K | null };

export function usePendingPoll<E, K extends string>({
  read, identity, isReady, readyBeat, runningBeat, terminalBeats, poll, clear, events, deps = [],
}: {
  read: () => E | null;
  identity: (entry: E) => string | number;
  isReady: (entry: E) => boolean;
  readyBeat: K;
  runningBeat: K;
  terminalBeats: readonly K[];
  /** Bir yoklama turu. Hazır olunca kaydı `ready` olarak yazmak çağıranın işi. */
  poll: (entry: E) => Promise<PollResult<K>>;
  clear: () => void;
  /** Aynı sekmede kaydı yeniden okutan olay adları. */
  events: readonly string[];
  deps?: readonly unknown[];
}) {
  const [beat, setBeat] = useState<Beat<E, K> | null>(null);
  const [version, setVersion] = useState(0);
  const eventKey = events.join("|");

  // Kayıt değişimini dinle: diğer sekmeler `storage`, aynı sekme özel olay.
  useEffect(() => {
    const bump = () => setVersion((v) => v + 1);
    const names = eventKey ? eventKey.split("|") : [];
    window.addEventListener("storage", bump);
    for (const name of names) window.addEventListener(name, bump);
    return () => {
      window.removeEventListener("storage", bump);
      for (const name of names) window.removeEventListener(name, bump);
    };
  }, [eventKey]);

  useEffect(() => {
    const stored = read();
    const id = stored ? identity(stored) : null;
    const ready = !!stored && isReady(stored);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- localStorage'daki bekleyen kaydı mount'ta ve her duyuruda okumak: tembel useState hydration uyuşmazlığı üretir (toast sunucuda yok), useSyncExternalStore ise yoklamanın yazdığı beat/dismissed ile çakışır. Bilinçli tek tohumlama.
    setBeat((b) => {
      // Kayıt yok: canlı beat kapanır, terminal beat kalır (yukarıdaki not).
      if (!stored) return b && terminalBeats.includes(b.kind) ? b : null;
      if (b && b.id === id) {
        // Aynı kayıt: hazır olduysa beat "ready"ye geçer (kapatma sayacı sıfır),
        // aksi hâlde olduğu gibi kalır (yoklayıcının yazdığı beat korunur).
        return ready && b.kind !== readyBeat ? { ...b, entry: stored, kind: readyBeat, dismissed: null } : b;
      }
      return { id: id as string | number, entry: stored, kind: ready ? readyBeat : runningBeat, dismissed: null };
    });
    if (!stored || ready) return;
    const pending: E = stored;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    async function check() {
      let result: PollResult<K>;
      try {
        result = await poll(pending);
      } catch {
        // Oturum değişti / sunucuya ulaşılamadı: kayıt bayat, izlemeyi bırak.
        result = { drop: true };
      }
      if (cancelled) return;
      if ("drop" in result) {
        clear();
        setBeat(null);
        return;
      }
      if ("beat" in result) {
        const kind = result.beat;
        setBeat((b) => ({ id: id as string | number, entry: read() ?? (b?.entry ?? pending), kind, dismissed: null }));
        return;
      }
      timer = setTimeout(check, POLL_INTERVAL_MS);
    }
    timer = setTimeout(check, FIRST_POLL_MS);
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `version` kaydın yeniden okunma tetikleyicisidir; çağıran fonksiyonlar (read/poll/...) sabit modül fonksiyonlarıdır, `deps` çağıranın yoklamayı bağladığı değerlerdir (locale, router).
  }, [version, ...deps]);

  const kind = beat?.kind ?? null;
  const visible = !!beat && beat.dismissed !== beat.kind;

  // Beat kapatma: "running" yalnızca kendini gizler (kayıt kalır, "ready" yine
  // gelir); diğer her beat terminaldir ve izlemeyi bitirir.
  const dismissBeat = useCallback(() => {
    if (kind === runningBeat) {
      setBeat((b) => (b ? { ...b, dismissed: runningBeat } : b));
      return;
    }
    clear();
    setBeat(null);
  }, [kind, runningBeat, clear]);

  // Öğrenci hedefe gitti (rapor / kütüphane): kayıt düşer, toast kapanır.
  const end = useCallback(() => {
    clear();
    setBeat(null);
  }, [clear]);

  return { entry: beat?.entry ?? null, kind, visible, dismissBeat, end };
}
